// Right-side tool panel (PX-386) — vertical Filters / Columns tabs on a rail,
// mirroring the reference. Filters = faceted checkbox sets (combine by AND
// across fields, OR within a field) + tri-states. Every filter section is
// collapsible (PX-448). Columns = show/hide.
import { useState, type ReactNode } from 'react'
import type { Facets, FacetOption, TableFilters } from './model'
import { COLUMN_LABELS } from './columns'
import { GroupByPanel } from './GroupByPanel'

export type PanelTab = 'group' | 'filters' | 'columns' | null

const RAIL_TABS: { id: Exclude<PanelTab, null>; label: string }[] = [
  { id: 'group', label: 'Group' },
  { id: 'filters', label: 'Filters' },
  { id: 'columns', label: 'Columns' },
]

export interface ColumnToggle {
  id: string
  visible: boolean
}

export function ToolPanel({
  tab,
  onTabChange,
  grouping,
  onGroupingChange,
  filters,
  onFiltersChange,
  facets,
  columns,
  onToggleColumn,
}: {
  tab: PanelTab
  onTabChange: (t: PanelTab) => void
  grouping: string[]
  onGroupingChange: (next: string[]) => void
  filters: TableFilters
  onFiltersChange: (next: TableFilters) => void
  facets: Facets
  columns: ColumnToggle[]
  onToggleColumn: (id: string) => void
}) {
  const rail = (
    <div className="flex w-9 shrink-0 flex-col gap-2 border-l border-ink-100 bg-ink-50 py-2">
      {RAIL_TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onTabChange(tab === t.id ? null : t.id)}
          style={{ writingMode: 'vertical-rl' }}
          className={`mx-auto rounded px-1 py-2 text-[11px] font-semibold uppercase tracking-wide transition ${
            tab === t.id ? 'bg-brand-500 text-white' : 'text-ink-500 hover:bg-ink-100'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  )

  return (
    <div className="flex">
      {tab && (
        <div className="w-72 shrink-0 overflow-y-auto border-l border-ink-100 bg-white p-3">
          {tab === 'group' && <GroupByPanel grouping={grouping} onChange={onGroupingChange} />}
          {tab === 'filters' && <FiltersTab filters={filters} onChange={onFiltersChange} facets={facets} />}
          {tab === 'columns' && <ColumnsTab columns={columns} onToggle={onToggleColumn} />}
        </div>
      )}
      {rail}
    </div>
  )
}

const COLLAPSE_KEY = 'pinxie-admin:photoshoots-filters-collapsed:v1'

// Tri-state for the boolean facets (Active / Trending); 'all'-anchored enum for
// Destination (PX-448). Both feed the same <Segmented> control.
const BOOL_TRISTATE: { v: boolean | null; t: string }[] = [
  { v: null, t: 'Any' },
  { v: true, t: 'Yes' },
  { v: false, t: 'No' },
]
const KIND_OPTIONS: { v: TableFilters['kind']; t: string }[] = [
  { v: 'all', t: 'Any' },
  { v: 'style', t: 'Styles' },
  { v: 'upscale', t: 'Upscale' },
]

function FiltersTab({
  filters,
  onChange,
  facets,
}: {
  filters: TableFilters
  onChange: (next: TableFilters) => void
  facets: Facets
}) {
  // Collapsed-section state persists on its OWN localStorage key so the
  // `Persisted` plumbing in PhotoshootsTable stays untouched. The set holds the
  // ids of collapsed sections (default = all expanded).
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? '[]') as string[])
    } catch {
      return new Set()
    }
  })
  const toggleSection = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next]))
      return next
    })
  }

  const toggle = (key: 'category' | 'tags' | 'model' | 'aspect', value: string) => {
    const cur = filters[key]
    const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]
    onChange({ ...filters, [key]: next })
  }
  const togglePlan = (value: string) => {
    const id = Number(value)
    const cur = filters.plan
    const next = cur.includes(id) ? cur.filter((v) => v !== id) : [...cur, id]
    onChange({ ...filters, plan: next })
  }

  return (
    <div className="flex flex-col">
      <Section id="active" title="Active" collapsed={collapsed} onToggle={toggleSection}>
        <Segmented value={filters.active} options={BOOL_TRISTATE} onChange={(v) => onChange({ ...filters, active: v })} />
      </Section>
      <Section id="trending" title="Trending" collapsed={collapsed} onToggle={toggleSection}>
        <Segmented value={filters.trending} options={BOOL_TRISTATE} onChange={(v) => onChange({ ...filters, trending: v })} />
      </Section>
      <Section id="kind" title="Destination" collapsed={collapsed} onToggle={toggleSection}>
        <Segmented value={filters.kind} options={KIND_OPTIONS} onChange={(v) => onChange({ ...filters, kind: v })} />
      </Section>
      {facets.category.length > 0 && (
        <Section id="category" title="Category" collapsed={collapsed} onToggle={toggleSection}>
          <FacetList options={facets.category} selected={filters.category} onToggle={(v) => toggle('category', v)} />
        </Section>
      )}
      {facets.tags.length > 0 && (
        <Section id="tags" title="Tags" collapsed={collapsed} onToggle={toggleSection}>
          <FacetList options={facets.tags} selected={filters.tags} onToggle={(v) => toggle('tags', v)} />
        </Section>
      )}
      {facets.model.length > 0 && (
        <Section id="model" title="Model" collapsed={collapsed} onToggle={toggleSection}>
          <FacetList options={facets.model} selected={filters.model} onToggle={(v) => toggle('model', v)} />
        </Section>
      )}
      {facets.plan.length > 0 && (
        <Section id="plan" title="Plan" collapsed={collapsed} onToggle={toggleSection}>
          <FacetList options={facets.plan} selected={filters.plan.map(String)} onToggle={togglePlan} />
        </Section>
      )}
      {facets.aspect.length > 0 && (
        <Section id="aspect" title="Formats" collapsed={collapsed} onToggle={toggleSection}>
          <FacetList options={facets.aspect} selected={filters.aspect} onToggle={(v) => toggle('aspect', v)} />
        </Section>
      )}
    </div>
  )
}

// Collapsible filter section — clickable header (title + chevron) over its body.
function Section({
  id,
  title,
  collapsed,
  onToggle,
  children,
}: {
  id: string
  title: string
  collapsed: Set<string>
  onToggle: (id: string) => void
  children: ReactNode
}) {
  const open = !collapsed.has(id)
  return (
    <div className="border-b border-ink-100 py-2 last:border-b-0">
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">{title}</span>
        <span className="text-xs text-ink-300">{open ? '▾' : '▸'}</span>
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  )
}

// Body-only segmented control (the section header owns the label now). Generic
// over the value type so it serves both the boolean tri-states and the
// Destination enum.
function Segmented<T>({
  value,
  options,
  onChange,
}: {
  value: T
  options: { v: T; t: string }[]
  onChange: (v: T) => void
}) {
  return (
    <div className="inline-flex gap-0.5 rounded-md bg-ink-100 p-0.5">
      {options.map((o) => (
        <button
          key={o.t}
          type="button"
          onClick={() => onChange(o.v)}
          className={`rounded px-2 py-0.5 text-xs font-medium transition ${
            value === o.v ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-900'
          }`}
        >
          {o.t}
        </button>
      ))}
    </div>
  )
}

// Body-only faceted checkbox list (the section header owns the title now).
function FacetList({
  options,
  selected,
  onToggle,
}: {
  options: FacetOption[]
  selected: string[]
  onToggle: (value: string) => void
}) {
  return (
    <div className="max-h-44 space-y-0.5 overflow-y-auto pr-1">
      {options.map((o) => (
        <label
          key={o.value}
          className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm text-ink-700 hover:bg-ink-50"
        >
          <input
            type="checkbox"
            checked={selected.includes(o.value)}
            onChange={() => onToggle(o.value)}
            className="h-3.5 w-3.5 rounded border-ink-300 text-brand-500 focus:ring-brand-200"
          />
          <span className="flex-1 truncate">{o.label}</span>
          <span className="shrink-0 text-xs text-ink-300">{o.count}</span>
        </label>
      ))}
    </div>
  )
}

function ColumnsTab({
  columns,
  onToggle,
}: {
  columns: ColumnToggle[]
  onToggle: (id: string) => void
}) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-400">Columns</div>
      <div className="space-y-0.5">
        {columns.map((c) => (
          <label
            key={c.id}
            className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm text-ink-700 hover:bg-ink-50"
          >
            <input
              type="checkbox"
              checked={c.visible}
              onChange={() => onToggle(c.id)}
              className="h-3.5 w-3.5 rounded border-ink-300 text-brand-500 focus:ring-brand-200"
            />
            <span className="flex-1 truncate">{COLUMN_LABELS[c.id] ?? c.id}</span>
          </label>
        ))}
      </div>
    </div>
  )
}
